import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { ProfileFetchRequest } from "./profilefetchrequest";

export class ProfileProps {
  public id: number = 0;
  public personal_info_id: number = 0;
  public honorific: number = 0;
  public first_name: string = "";
  public middle_name: string = "";
  public last_name: string = "";
  public first_name_mr: string = "";
  public middle_name_mr: string = "";
  public last_name_mr: string = "";
  public email_id: string = "";
  public mobile_no: string = "";
  public profile_pic_path: string = "";
  public source_table: string = "";
  public under_supervision_name: string = "";

  public readonly is_newly_created: boolean = false;

  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new ProfileProps(true);
  }
}

export class Profile implements IPersistable<Profile> {
  public static readonly mastertablename: string = "profile";

  private constructor(
    public readonly p: ProfileProps,
    public readonly AllowEdit: boolean,
  ) {}

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): Profile {
    let newProfile: ProfileProps = Utils.GetInstance().DeepCopy(this.p);
    return Profile.CreateInstance(newProfile, true);
  }

  public static CreateNewInstance() {
    return new Profile(ProfileProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new Profile(data as ProfileProps, allowEdit);
  }

  public CheckSaveValidity(
    _td: TransportData,
    vra: ValidationResultAccumulator,
  ): void {
    if (!this.AllowEdit)
      vra.add("", "This object is not editable and hence cannot be saved.");
    // if (this.p.ProfileRef <= 0) vra.add('ProfileRef', 'Profile cannot be blank. ');
    // if (this.p.CityRef <= 0) vra.add('CityRef', 'City cannot be blank. ');
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(
      td.MainData,
      Profile.mastertablename,
      this.p,
    );
  }

  private static m_currentInstance: Profile = Profile.CreateNewInstance();

  public static GetCurrentInstance() {
    return Profile.m_currentInstance;
  }

  public static SetCurrentInstance(value: Profile) {
    Profile.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): Profile {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, Profile.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, Profile.mastertablename)!
        .Entries) {
        return Profile.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(
    cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name",
  ): Profile[] {
    let result: Profile[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, Profile.mastertablename)) {
      let coll = dcs.GetCollection(cont, Profile.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate))
        entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(Profile.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): Profile[] {
    return Profile.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(
    req: ProfileFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr =
      await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(
    id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new ProfileFetchRequest();
    req.profile_ids.push(id);

    let tdResponse = (await Profile.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Profile.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(
    req: ProfileFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdResponse = (await Profile.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Profile.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new ProfileFetchRequest();
    let tdResponse = (await Profile.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Profile.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByLoginRef(
    login_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new ProfileFetchRequest();
    req.login_id = login_id;
    let tdResponse = (await Profile.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Profile.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(
    successHandler: () => Promise<void> = null!,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr =
      await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    } else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }
}
